import type { LucideIcon } from 'lucide-react';
import {
  AppWindow,
  Boxes,
  CircleQuestionMark,
  Container,
  Cpu,
  Disc,
  File,
  FolderOpen,
  LibraryBig,
  Smartphone,
} from 'lucide-react';

import { AssetType } from '@features/applications/applications.models';

export interface AssetTypeMetadata {
  /**
   * Description of the asset type
   */
  description: string;
  /**
   * Lucide icon component
   */
  icon: LucideIcon;
  /**
   * Short display text used in compact contexts like labels. Falls back to
   * {@link text} when not provided.
   */
  shortText?: string;
  /**
   * Display text for the asset type
   */
  text: string;
}

export const assetTypeMetadata: Record<AssetType, AssetTypeMetadata> = {
  [AssetType.Application]: {
    description: 'A software application',
    icon: AppWindow,
    text: 'Application',
  },
  [AssetType.Container]: {
    description: 'A container image belonging to an application or module',
    icon: Container,
    text: 'Container',
  },
  [AssetType.Device]: {
    description: 'Software installed on a device',
    icon: Smartphone,
    text: 'Device',
  },
  [AssetType.File]: {
    description:
      'A file belonging to a software application (typically not part of a library)',
    icon: File,
    text: 'File',
  },
  [AssetType.Filesystem]: {
    description: 'A file system image or structure',
    icon: FolderOpen,
    text: 'File System',
  },
  [AssetType.Firmware]: {
    description: 'Code embedded in a device',
    icon: Cpu,
    text: 'Firmware',
  },
  [AssetType.Library]: {
    description:
      'A reusable software library/package, operating system, or framework used in an application, container, device, or firmware',
    icon: LibraryBig,
    text: 'Library',
  },
  [AssetType.Module]: {
    description:
      "A reusable modular piece of software that isn't a full application or library",
    icon: Boxes,
    text: 'Module',
  },
  [AssetType.VirtualMachineDisk]: {
    description:
      'A virtual machine disk (VMDK) that contains a file system, operating system, applications, and libraries',
    icon: Disc,
    shortText: 'VMDK',
    text: 'Virtual Machine Disk',
  },
  [AssetType.Unknown]: {
    description: 'Asset type is unknown or not specified',
    icon: CircleQuestionMark,
    text: 'Unknown',
  },
};

/**
 * Get metadata for an asset type, with fallback to Unknown if not found
 */
export const getAssetTypeMetadata = (
  type: AssetType | undefined,
): AssetTypeMetadata => assetTypeMetadata[type ?? AssetType.Unknown];
